import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  AssignmentInd as AssignmentIndIcon,
} from '@mui/icons-material';
import {
  getRoles,
  getModules,
  getMiniModules,
  getAccessCodes,
  getProfiles,
  createProfile,
  updateProfile,
  deleteProfile,
  Role,
  Module,
  MiniModule,
  Access,
  Profile,
  ProfileCreate,
} from '../../../services/rbac';
import useAlert from '../../../hooks/useAlert';
import { extractErrorMessage } from '../../../utils/errorHandler';
import AlertDialog from '../../../components/AlertDialog';

interface PermissionMap {
  [miniModuleId: number]: {
    view: boolean;
    add: boolean;
    edit: boolean;
    delete: boolean;
  };
}

interface PermissionDialogState {
  open: boolean;
  role: number | null;
  permissions: PermissionMap;
}

const AccessControlManagement: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [miniModules, setMiniModules] = useState<MiniModule[]>([]);
  const [accessCodes, setAccessCodes] = useState<Access[]>([]);
  const [permissionDialog, setPermissionDialog] = useState<PermissionDialogState>({
    open: false,
    role: null,
    permissions: {},
  });
  const [loading, setLoading] = useState(false);
  const [expandedModules, setExpandedModules] = useState<number[]>([]);

  const { alertState, showError, showSuccess, showInfo, hideAlert } = useAlert();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesData, modulesData, miniModsData, accessData] = await Promise.all([
        getRoles(),
        getModules(),
        getMiniModules(),
        getAccessCodes(),
      ]);
      setRoles(rolesData);
      setModules(modulesData);
      setMiniModules(miniModsData);
      setAccessCodes(accessData);
    } catch (error: any) {
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load existing permissions for selected role
   */
  const loadRolePermissions = async (roleId: number) => {
    if (!roleId) {
      setPermissionDialog((prev) => ({ ...prev, permissions: {} }));
      return;
    }

    try {
      // Fetch all profiles for this role
      const roleProfiles = await getProfiles(roleId);

      // Build permissions object from profiles
      const permissions: PermissionMap = {};
      roleProfiles.forEach((profile) => {
        const accessCode = profile.access?.code || '0000';
        permissions[profile.mini_module_id] = {
          view: accessCode[3] === '1', // Position 0 from right
          add: accessCode[2] === '1', // Position 1 from right
          edit: accessCode[1] === '1', // Position 2 from right
          delete: accessCode[0] === '1', // Position 3 from right
        };
      });

      setPermissionDialog((prev) => ({ ...prev, permissions }));
    } catch (error: any) {
      console.error('Error loading role permissions:', error);
      showError('Erreur lors du chargement des permissions existantes');
    }
  };

  /**
   * Check if all mini-modules have a specific permission (Global level)
   */
  const isAllGlobalSelected = (permissionType: 'view' | 'add' | 'edit' | 'delete'): boolean => {
    if (miniModules.length === 0) return false;
    return miniModules.every(
      (mm) => permissionDialog.permissions[mm.id]?.[permissionType] === true
    );
  };

  /**
   * Check if all mini-modules in a module have a specific permission
   */
  const isAllModuleSelected = (moduleId: number, permissionType: 'view' | 'add' | 'edit' | 'delete'): boolean => {
    const moduleMiniModules = miniModules.filter((mm) => mm.module_id === moduleId);
    if (moduleMiniModules.length === 0) return false;
    return moduleMiniModules.every(
      (mm) => permissionDialog.permissions[mm.id]?.[permissionType] === true
    );
  };

  /**
   * Handle global permission change (applies to ALL mini-modules)
   */
  const handleGlobalPermissionChange = (permissionType: 'view' | 'add' | 'edit' | 'delete', checked: boolean) => {
    const newPermissions = { ...permissionDialog.permissions };

    miniModules.forEach((mm) => {
      if (!newPermissions[mm.id]) {
        newPermissions[mm.id] = { view: false, add: false, edit: false, delete: false };
      }
      newPermissions[mm.id][permissionType] = checked;
    });

    setPermissionDialog({ ...permissionDialog, permissions: newPermissions });
  };

  /**
   * Handle module-level permission change (applies to all mini-modules in this module)
   */
  const handleModulePermissionChange = (moduleId: number, permissionType: 'view' | 'add' | 'edit' | 'delete', checked: boolean) => {
    const newPermissions = { ...permissionDialog.permissions };
    const moduleMiniModules = miniModules.filter((mm) => mm.module_id === moduleId);

    moduleMiniModules.forEach((mm) => {
      if (!newPermissions[mm.id]) {
        newPermissions[mm.id] = { view: false, add: false, edit: false, delete: false };
      }
      newPermissions[mm.id][permissionType] = checked;
    });

    setPermissionDialog({ ...permissionDialog, permissions: newPermissions });
  };

  /**
   * Handle individual mini-module permission change
   */
  const handleMiniModulePermissionChange = (
    miniModuleId: number,
    permissionType: 'view' | 'add' | 'edit' | 'delete',
    checked: boolean
  ) => {
    const newPermissions = { ...permissionDialog.permissions };

    if (!newPermissions[miniModuleId]) {
      newPermissions[miniModuleId] = { view: false, add: false, edit: false, delete: false };
    }

    newPermissions[miniModuleId][permissionType] = checked;

    setPermissionDialog({ ...permissionDialog, permissions: newPermissions });
  };

  /**
   * Open permission assignment dialog
   */
  const handleOpenDialog = () => {
    setPermissionDialog({
      open: true,
      role: null,
      permissions: {},
    });
    // Expand all modules by default
    setExpandedModules(modules.map((m) => m.id));
  };

  /**
   * Close permission assignment dialog
   */
  const handleCloseDialog = () => {
    setPermissionDialog({
      open: false,
      role: null,
      permissions: {},
    });
    setExpandedModules([]);
  };

  /**
   * Handle accordion expand/collapse
   */
  const handleAccordionChange = (moduleId: number) => {
    setExpandedModules((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  };

  /**
   * Save permissions (create/update/delete profiles as needed)
   */
  const handleAssignPermission = async () => {
    const { role, permissions } = permissionDialog;

    if (!role) {
      showError('Veuillez sélectionner un rôle');
      return;
    }

    try {
      setLoading(true);

      // Step 1: Fetch existing profiles for this role
      const existingProfiles = await getProfiles(role);

      const operations = {
        toCreate: [] as ProfileCreate[],
        toUpdate: [] as { id: number; access_id: number }[],
        toDelete: [] as number[],
      };

      // Step 2: Process each mini-module permission
      Object.keys(permissions).forEach((miniModuleIdStr) => {
        const miniModuleId = parseInt(miniModuleIdStr);
        const perms = permissions[miniModuleId];
        const hasAnyPermission = perms.view || perms.add || perms.edit || perms.delete;

        // Encode permissions back to access code
        const accessCode =
          (perms.delete ? '1' : '0') +
          (perms.edit ? '1' : '0') +
          (perms.add ? '1' : '0') +
          (perms.view ? '1' : '0');

        const accessCodeObj = accessCodes.find((ac) => ac.code === accessCode);
        const miniModule = miniModules.find((mm) => mm.id === miniModuleId);

        if (!miniModule || !accessCodeObj) return;

        // Find existing profile for this role-module-minimodule combination
        const existingProfile = existingProfiles.find((p) => p.mini_module_id === miniModuleId);

        if (existingProfile) {
          if (hasAnyPermission) {
            // UPDATE: Profile exists and has permissions
            if (existingProfile.access_id !== accessCodeObj.id) {
              operations.toUpdate.push({
                id: existingProfile.id,
                access_id: accessCodeObj.id,
              });
            }
          } else {
            // DELETE: Profile exists but no permissions selected
            operations.toDelete.push(existingProfile.id);
          }
        } else {
          if (hasAnyPermission) {
            // CREATE: New profile with permissions
            operations.toCreate.push({
              role_id: role,
              module_id: miniModule.module_id,
              mini_module_id: miniModuleId,
              access_id: accessCodeObj.id,
            });
          }
        }
      });

      // Step 3: Execute all operations
      const promises: Promise<any>[] = [];

      operations.toCreate.forEach((permData) => {
        promises.push(createProfile(permData));
      });

      operations.toUpdate.forEach((updateData) => {
        promises.push(
          updateProfile(updateData.id, {
            access_id: updateData.access_id,
          })
        );
      });

      operations.toDelete.forEach((profileId) => {
        promises.push(deleteProfile(profileId));
      });

      if (promises.length === 0) {
        showInfo('Aucune modification à enregistrer');
        handleCloseDialog();
        return;
      }

      await Promise.all(promises);

      const totalChanges = operations.toCreate.length + operations.toUpdate.length + operations.toDelete.length;
      showSuccess(
        `Permissions mises à jour avec succès: ${operations.toCreate.length} créées, ${operations.toUpdate.length} modifiées, ${operations.toDelete.length} supprimées (${totalChanges} au total)`
      );

      handleCloseDialog();
    } catch (error: any) {
      console.error('Error managing permissions:', error);
      showError(extractErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const moduleColors: { [key: string]: string } = {
    Admin: '#F44336',
    Stock: '#2196F3',
    Config: '#1976D2',
    GRC: '#9C27B0',
    Prod: '#FF9800',
    Logistique: '#607D8B',
  };

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Gestion des Accès & Permissions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AssignmentIndIcon />}
            onClick={handleOpenDialog}
            disabled={loading}
          >
            Attribuer des Permissions
          </Button>
        </Box>

        <Typography variant="body1" color="text.secondary">
          Utilisez cette interface pour attribuer des permissions à un rôle. Sélectionnez un rôle, puis cochez les
          permissions souhaitées au niveau global, par module, ou individuellement par sous-module.
        </Typography>
      </Paper>

      {/* Permission Assignment Dialog */}
      <Dialog
        open={permissionDialog.open}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Attribuer des Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Sélectionnez un rôle et configurez les permissions CRUD pour chaque module
          </Typography>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          {/* Role Selection */}
          <FormControl fullWidth margin="dense" sx={{ mb: 3 }}>
            <InputLabel>Rôle</InputLabel>
            <Select
              value={permissionDialog.role || ''}
              onChange={(e) => {
                const roleId = Number(e.target.value);
                setPermissionDialog({ ...permissionDialog, role: roleId });
                loadRolePermissions(roleId);
              }}
              label="Rôle"
            >
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {permissionDialog.role && (
            <>
              {/* Global Permissions */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  border: '2px solid #4caf50',
                  borderRadius: 2,
                  backgroundColor: '#f1f8f4',
                }}
              >
                <Typography variant="h6" fontWeight="bold" color="#4caf50" sx={{ mb: 2 }}>
                  Permissions Globales (Tous les Modules)
                </Typography>
                <FormGroup row sx={{ gap: 3 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllGlobalSelected('view')}
                        onChange={(e) => handleGlobalPermissionChange('view', e.target.checked)}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                      />
                    }
                    label={
                      <Typography fontWeight="bold" color="#4caf50">
                        Tout Consulter
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllGlobalSelected('add')}
                        onChange={(e) => handleGlobalPermissionChange('add', e.target.checked)}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                      />
                    }
                    label={
                      <Typography fontWeight="bold" color="#4caf50">
                        Tout Ajouter
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllGlobalSelected('edit')}
                        onChange={(e) => handleGlobalPermissionChange('edit', e.target.checked)}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                      />
                    }
                    label={
                      <Typography fontWeight="bold" color="#4caf50">
                        Tout Modifier
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isAllGlobalSelected('delete')}
                        onChange={(e) => handleGlobalPermissionChange('delete', e.target.checked)}
                        sx={{ '& .MuiSvgIcon-root': { fontSize: 20 } }}
                      />
                    }
                    label={
                      <Typography fontWeight="bold" color="#4caf50">
                        Tout Supprimer
                      </Typography>
                    }
                  />
                </FormGroup>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Module-Level Permissions */}
              <Typography variant="h6" gutterBottom>
                Permissions par Module
              </Typography>

              {modules.map((module) => {
                const moduleMiniModules = miniModules.filter((mm) => mm.module_id === module.id);
                const isExpanded = expandedModules.includes(module.id);

                if (moduleMiniModules.length === 0) return null;

                return (
                  <Accordion
                    key={module.id}
                    expanded={isExpanded}
                    onChange={() => handleAccordionChange(module.id)}
                    sx={{ mb: 2 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: moduleColors[module.name] || '#64748b',
                        color: 'white',
                        '& .MuiAccordionSummary-expandIconWrapper': {
                          color: 'white',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        {isExpanded ? <FolderOpenIcon /> : <FolderIcon />}
                        <Typography variant="h6" fontWeight="bold">
                          {module.name}
                        </Typography>
                        <Chip
                          label={`${moduleMiniModules.length} sous-modules`}
                          size="small"
                          sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
                        />
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      {/* Module-level Select All */}
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.5,
                          backgroundColor: '#e3f2fd',
                          borderRadius: 1,
                          border: '1px solid #1976d2',
                        }}
                      >
                        <Typography variant="body2" fontWeight="bold" color="#1976d2" sx={{ mb: 1 }}>
                          Tout sélectionner pour {module.name}:
                        </Typography>
                        <FormGroup row sx={{ gap: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isAllModuleSelected(module.id, 'view')}
                                onChange={(e) =>
                                  handleModulePermissionChange(module.id, 'view', e.target.checked)
                                }
                              />
                            }
                            label={<Typography variant="body2" fontWeight="medium">Tout Consulter</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isAllModuleSelected(module.id, 'add')}
                                onChange={(e) =>
                                  handleModulePermissionChange(module.id, 'add', e.target.checked)
                                }
                              />
                            }
                            label={<Typography variant="body2" fontWeight="medium">Tout Ajouter</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isAllModuleSelected(module.id, 'edit')}
                                onChange={(e) =>
                                  handleModulePermissionChange(module.id, 'edit', e.target.checked)
                                }
                              />
                            }
                            label={<Typography variant="body2" fontWeight="medium">Tout Modifier</Typography>}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={isAllModuleSelected(module.id, 'delete')}
                                onChange={(e) =>
                                  handleModulePermissionChange(module.id, 'delete', e.target.checked)
                                }
                              />
                            }
                            label={<Typography variant="body2" fontWeight="medium">Tout Supprimer</Typography>}
                          />
                        </FormGroup>
                      </Box>

                      {/* Individual Mini-Module Checkboxes */}
                      {moduleMiniModules.map((miniModule) => {
                        const miniModulePerms = permissionDialog.permissions[miniModule.id] || {
                          view: false,
                          add: false,
                          edit: false,
                          delete: false,
                        };

                        return (
                          <Box
                            key={miniModule.id}
                            sx={{
                              mb: 2,
                              p: 2,
                              border: '1px solid #e0e0e0',
                              borderRadius: 1,
                              backgroundColor: '#ffffff',
                            }}
                          >
                            <Box display="flex" alignItems="center" mb={1}>
                              <FolderOpenIcon sx={{ mr: 1, fontSize: 18, color: '#666' }} />
                              <Typography variant="body1" fontWeight="medium">
                                {miniModule.name}
                              </Typography>
                            </Box>
                            <FormGroup row sx={{ ml: 3 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={miniModulePerms.view}
                                    onChange={(e) =>
                                      handleMiniModulePermissionChange(
                                        miniModule.id,
                                        'view',
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Consulter"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={miniModulePerms.add}
                                    onChange={(e) =>
                                      handleMiniModulePermissionChange(
                                        miniModule.id,
                                        'add',
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Ajouter"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={miniModulePerms.edit}
                                    onChange={(e) =>
                                      handleMiniModulePermissionChange(
                                        miniModule.id,
                                        'edit',
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Modifier"
                              />
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={miniModulePerms.delete}
                                    onChange={(e) =>
                                      handleMiniModulePermissionChange(
                                        miniModule.id,
                                        'delete',
                                        e.target.checked
                                      )
                                    }
                                  />
                                }
                                label="Supprimer"
                              />
                            </FormGroup>
                          </Box>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Annuler
          </Button>
          <Button
            onClick={handleAssignPermission}
            variant="contained"
            disabled={!permissionDialog.role || loading}
          >
            {loading ? 'Enregistrement...' : 'Enregistrer les Permissions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default AccessControlManagement;
